import type { WorkoutLogPill } from "../../api/types";
import { decodeArtifact, type CoachArtifact } from "./artifacts/types";
import { CONNECTION_FAILED_LINE, streamErrorLine } from "./chatFailure";

/**
 * What the reader announces, in the order the stream says it. The shell decides
 * what each means for the message list; the reader holds no reveal buffer.
 *
 * Final content only (S24): a client shows nothing of a turn that did not
 * reach `done`. The deltas are the one provisional thing handed on as they come
 * (the shell piles them up unseen until `onDone`); every card is announced
 * final, which is why the reader holds the ones that arrive early.
 */
export interface CoachStreamSink {
  /** A piece of the reply. Provisional: it is the reply only once `onDone` follows, and `onError` takes it back. */
  onDelta: (chunk: string) => void;
  onFollowups: (f: (string | WorkoutLogPill)[]) => void;
  /**
   * A card that decoded, and is final. An unknown `type` or `v` decodes to nothing and is dropped. A card that
   * arrives before `done` is a tool round's, provisional until `done` (an error discards it with the reply): the
   * reader holds it and announces it, in arrival order, just before `onDone`. A card after `done` is announced
   * as it arrives.
   */
  onArtifact: (artifact: CoachArtifact) => void;
  /**
   * The turn ended without a reply, and the line is what to show in its place: the stream's `error` event, or a
   * body that ended with neither `done` nor `error` (the contract's `cut`: `CONNECTION_FAILED_LINE`). What was
   * announced before it is discarded, the deltas; the cards that came before `done` were never announced.
   * Nothing follows it.
   */
  onError: (line: string) => void;
  /** The reply is complete: the stream's first `done`. Called once; everything announced after it is final. */
  onDone: () => void;
}

export interface CoachStreamReader {
  /**
   * Feed the next piece of the decoded body, however the network split it.
   * Returns `false` once the turn has ended on an error: stop reading, nothing
   * after it is read.
   */
  push: (text: string) => boolean;
  /**
   * The body ended. One that reached `done` or an `error` has nothing more to say; one that ended before either
   * is cut (the contract's ending of that name): nothing that arrived is final, so it is announced as an error
   * and the reply is never revealed.
   */
  end: () => void;
}

/**
 * The chat stream's parser, as a pure function of the text it is fed (#448).
 *
 * Lifted out of `useCoachStream` so the recorded transcripts of
 * `contracts/coach-stream-transcripts/` are replayed through the very code the
 * page runs (`coachStreamTranscripts.test.ts`). The grammar it reads is
 * `contracts/coach-stream.json`: frames end on a blank line, comments
 * (`: open`, `: hb`) are skipped, and each event is told apart by its one key
 * (`content`, `artifact`, `error`, `followups`).
 *
 * How it reads finality, as the contract states it (S24, #449): deltas are
 * handed on as they come but are the reply only at `done` (the shell reveals on
 * `onDone`); a card that comes before `done` is held to it, and dropped with
 * the deltas by an `error`; an `error` ends the turn at once and nothing after
 * it is read, the one `done` a transport failure sends after its error
 * included; and a body that ends with neither is a cut stream, which shows the
 * connection line and none of what arrived. Once `done` is announced only
 * cards and follow-ups are read: the reply cannot change, and a stream that
 * outlives `done` may by then be talking to a page that has moved on.
 */
export function createCoachStreamReader(sink: CoachStreamSink): CoachStreamReader {
  let sseBuffer = "";
  // `done` was announced: the reply is final.
  let answered = false;
  // The turn ended without a reply: an `error` event, or a body cut short.
  let failed = false;
  // The cards that came before `done`: a tool round's, provisional until `done`.
  let held: CoachArtifact[] = [];

  return {
    push(text: string): boolean {
      if (failed) return false;
      sseBuffer += text;

      const events = sseBuffer.split("\n\n");
      sseBuffer = events.pop() ?? "";

      for (const event of events) {
        const line = event.trim();
        if (!line.startsWith("data: ")) continue;

        try {
          // Every field stays optional. That is R5b, not laxness: the
          // artifact line carries no `done` key, unlike every other event
          // the coach emits, so required-key decoding would throw on the
          // first card.
          const data = JSON.parse(line.slice(6)) as {
            content?: string;
            done?: boolean;
            error?: string;
            // Only ever `true`, on the one error the server sends when the
            // request pool was exhausted after the stream began (#392).
            busy?: boolean;
            followups?: (string | WorkoutLogPill)[];
            artifact?: unknown;
          };

          // After `done` the reply is final and only cards and follow-ups can follow (the contract's `answered`
          // state). A delta or an error there is out of grammar; it is not read, because it would land on
          // whatever turn the page is showing by then (the person may have asked the next question).
          if (data.error && !answered) {
            // The cards held go with the deltas: the shell never heard of them.
            held = [];
            sink.onError(streamErrorLine({ error: data.error, busy: data.busy }));
            failed = true;
            return false;
          }

          if (data.followups && Array.isArray(data.followups)) {
            sink.onFollowups(data.followups as (string | WorkoutLogPill)[]);
          }

          // A card is on screen only once it is final: one after `done` as it
          // arrives, one before it at `done`. An unknown type or an
          // unrecognised `v` decodes to null and is dropped silently rather
          // than throwing.
          if (data.artifact !== undefined) {
            const artifact = decodeArtifact(data.artifact);
            if (artifact) {
              if (answered) sink.onArtifact(artifact);
              else held.push(artifact);
            }
          }

          if (data.content && !answered) {
            sink.onDelta(data.content);
          }

          // First time the server signals `done`, the message body is complete.
          // Reveal immediately so the user sees the text and the cards that
          // came before it, then keep reading: the cards picked for the reply
          // and the followups event are sent later (after async generation),
          // and the connection closes naturally once they have been.
          if (data.done && !answered) {
            answered = true;
            for (const artifact of held) sink.onArtifact(artifact);
            held = [];
            sink.onDone();
          }
        } catch {
          // Skip unparseable chunks
        }
      }

      return true;
    },

    end(): void {
      // A body that ended before `done` and before an `error` is cut: the
      // server always ends a turn with one of them, so the stream broke in
      // transit and nothing that arrived is final (the contract's `cut`).
      if (failed || answered) return;
      failed = true;
      held = [];
      sink.onError(CONNECTION_FAILED_LINE);
    },
  };
}
